import InviteModal from "@components/InviteModal";
import RequestDiscussion from "@components/RequestDiscussion/RequestDiscussion";
import RequestFAQs from "@components/RequestFAQs";
import { RequestPageBanner } from "@components/RequestPageComponents";
import RequestPageTagsContainer from "@components/RequestPageComponents/RequestPageTagsContainer";
import TRLs from "@components/TRLs";
import toast from "@components/Toast";
import { Icon, PageContainer, Tag, TextLink, Tooltip } from "@components/library";
import NotifyModal from "@components/modals/NotifyModal";
import ContentClipFade from "@components/shared/ContentClipFade/ContentClipFade";
import { COLORS, FONTS, WINDOW_DIMENSIONS } from "@constants";
import { toggleLoginModal } from "@redux/actions/profileActions";
import { RootState } from "@redux/store";
import { getRequestForStartup } from "@requests/requestForStartups";
import { marketplacePath } from "@routes/utils";
import { useQuery } from "@tanstack/react-query";
import { SegmentEventName } from "@tsTypes/__generated__/enums";
import { LaunchableRequestStatus, REQUEST_PARTNER_TYPE_LABELS } from "@tsTypes/requests";
import { UserRole } from "@tsTypes/users";
import { getArrayFromRubyRange, track } from "@utils/appUtils";
import { setPrevUrlCookie, setSignupPrefillEmailCookie } from "@utils/authUtils";
import { confidentialityAwareCompanyName } from "@utils/companyUtils";
import { t } from "@utils/i18n";
import { getLaunchableRequestStatus } from "@utils/requestUtils";
import { toastErrorHandler } from "@utils/requests";
import { renderMarkdownText, renderMarkdownTextWithDangerousHTML } from "@utils/textUtils";
import { userShortName } from "@utils/userUtils";
import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory, useParams } from "react-router-dom";
import { Loader } from "semantic-ui-react";
import styled from "styled-components";
import AboutCompany from "../../components/RequestPageComponents/AboutCompany";
import Benefits from "../RFPPage/Benefits";
import { TRLsContainer, TrlTagContent } from "../RFPPage/RfpPage";
import ProgressiveGate from "../RFPPage/progressive/ProgressiveGate";
import { GateContainer, RightColumnFade, useProgressiveDisclosure } from "../shared/progressive";
import DataPoints from "./DataPoints";
import Cta from "./RfsCta";
import Sidebar from "./Sidebar";

export default function RequestForStartupPage() {
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);
  const [isCommenting, setIsCommenting] = useState(false);

  const currentUser = useSelector((state: RootState) => state.profiles.currentUser);
  const isTabletLandscape = useSelector((state: RootState) => state.window.isTabletLandscape);
  const { slug } = useParams();
  const history = useHistory();
  const dispatch = useDispatch();

  const { data: request, isLoading } = useQuery(
    ["getRequestForStartup", slug],
    () => getRequestForStartup(slug),
    {
      enabled: Boolean(slug),
      onError: toastErrorHandler,
    }
  );

  useEffect(() => {
    if (!request) return;

    document.title = `${request.title} | Startup Program | Halo`;
  }, [request]);

  const backgroundRef = useRef<HTMLDivElement>(null);
  const lookingForRef = useRef<HTMLDivElement>(null);
  const benefitsRef = useRef<HTMLDivElement>(null);
  const whoWeAreRef = useRef<HTMLDivElement>(null);
  const qaRef = useRef<HTMLDivElement>(null);
  const gateTopRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!request?.id) {
      return;
    }

    track(SegmentEventName.VIEW, {
      request_id: request.id,
      requestable: {
        requestable_id: request.id,
        requestable_type: request.requestable_type,
      },
      react_component: "RequestForStartupPage",
    });
  }, [request?.id]);

  useLayoutEffect(() => {
    let animationFrameId: number;

    const checkAndScroll = () => {
      if (window.location.hash === "#qa" && qaRef.current) {
        qaRef.current.scrollIntoView({ behavior: "smooth" });
      } else {
        animationFrameId = requestAnimationFrame(checkAndScroll);
      }
    };

    checkAndScroll();

    return () => {
      if (animationFrameId) {
        cancelAnimationFrame(animationFrameId);
      }
    };
  }, [qaRef]);

  const { shouldGate, topOffset, paywallJsonLd } = useProgressiveDisclosure(request?.title);
  const companyName = confidentialityAwareCompanyName(
    request?.company,
    Boolean(request?.confidential)
  );

  if (isLoading || !request) {
    return <Loader inline="centered" />;
  }

  const trlRange = request.requestable.trl_range
    ? getArrayFromRubyRange(request.requestable.trl_range)
    : null;

  const detailsContent = (
    <>
      <Section>
        <div>
          <Subheading first>
            {t("pages.request_for_startups_page.headings.eligible_country_codes.heading")}
          </Subheading>
          {request.requestable.eligible_country_names &&
          request.requestable.eligible_country_names.length > 1 ? (
            <>
              <div style={{ marginBottom: "5px" }}>
                {t("pages.request_for_startups_page.headings.eligible_country_codes.list_heading")}
              </div>
              <PlainList>
                {request.requestable.eligible_country_names.map((country) => (
                  <li key={country}>{country}</li>
                ))}
              </PlainList>
            </>
          ) : (
            <PlainList>
              <li>
                {t("pages.request_for_startups_page.headings.eligible_country_codes.body", {
                  locations: request.requestable.eligible_country_names?.length
                    ? request.requestable.eligible_country_names.join(", ")
                    : t("pages.request_for_startups_page.headings.eligible_country_codes.all"),
                })}
              </li>
            </PlainList>
          )}
        </div>
        {Boolean(request.requestable.eligible_startup_stages?.length) && (
          <div>
            <Subheading>
              {t("pages.request_for_startups_page.headings.eligible_startup_stages.heading")}
            </Subheading>
            <div style={{ marginBottom: "5px" }}>
              {t("pages.request_for_startups_page.headings.eligible_startup_stages.list_heading")}
            </div>
            <PlainList>
              {request.requestable.eligible_startup_stages!.map((stage) => (
                <li key={`stage-${stage}`}>{stage}</li>
              ))}
            </PlainList>
          </div>
        )}
        {trlRange ? (
          <TRLsContainer>
            <Subheading>{t("pages.request_for_startups_page.trl.heading")}</Subheading>
            <ListHeading style={{ margin: "-15px 0 10px" }}>
              {t("pages.request_for_startups_page.trl.list_heading")}
            </ListHeading>
            <Tag
              content={
                <TrlTagContent>
                  {trlRange[0] === trlRange[1]
                    ? `Level ${trlRange[0]}`
                    : `Levels ${trlRange[0]}-${trlRange[1]}`}
                  <Tooltip
                    content={t("pages.request_for_startups_page.trl.tooltip")}
                    tooltipWidth="410px"
                  >
                    <Icon name="Info" size="xs" color={COLORS.NEUTRAL_400} />
                  </Tooltip>
                </TrlTagContent>
              }
              size="md"
              margin="-10px 0 10px 0"
            />
            <TRLs trlRange={trlRange} />
          </TRLsContainer>
        ) : null}
      </Section>

      <AnchorSpan ref={benefitsRef} topOffset={topOffset}>
        <Benefits
          partnershipModels={[]}
          fundingBenefits={request.funding_benefits}
          nonMonetaryBenefits={request.non_monetary_benefits}
          withMarginTop={false}
        />
      </AnchorSpan>

      <AnchorSpan ref={whoWeAreRef} topOffset={topOffset}>
        <AboutCompany
          withMarginTop={false}
          request={request}
          customText={request.requestable.who_we_are ?? undefined}
          lineHeight="1.7"
        />
      </AnchorSpan>
      <AnchorDiv ref={qaRef} topOffset={topOffset}>
        <RequestDiscussion
          request={request}
          isCommenting={isCommenting}
          setIsCommenting={setIsCommenting}
          setIsShareModalOpen={setIsShareModalOpen}
        />
      </AnchorDiv>
    </>
  );

  return (
    <>
      <InviteModal
        open={isShareModalOpen}
        requestId={request.id}
        closeModal={() => setIsShareModalOpen(false)}
        trackAndToastHandler={() => toast.success("Startup program shared")}
        inviteMessage=""
        inviteUrl="/notifications/share_rfp"
        modalHeader={`Share this partnering request with ${
          currentUser.role === UserRole.UNIVERSITY_ADMIN ? "faculty" : "colleagues"
        }`}
        modalSubheader={`Your ${
          currentUser.role === UserRole.UNIVERSITY_ADMIN ? "faculty" : "colleague(s)"
        } will receive an email with a link to this partnering request`}
        inviteSubject={`${
          currentUser?.first_name ? userShortName(currentUser) : "Someone"
        } shared a partnering request with you.`}
        typeOfInvite="shareRFP"
      />
      <NotifyModal
        isOpen={
          getLaunchableRequestStatus(request as any) === LaunchableRequestStatus.COMING_SOON &&
          !request.requestable.accessible_before_launch_date
        }
        onClose={() => history.push(marketplacePath("requests"))}
        request={request}
      />
      <PageContainer hasPaddingTop={false} maxWidth="1680px">
        <Breadcrumb>
          <TextLink to={marketplacePath("requests")} font={FONTS.MEDIUM_2}>
            <Icon name="Chevron Left" margin="0 0 0 -8px" />
            {t("pages.request_for_startups_page.breadcrumb")}
          </TextLink>
        </Breadcrumb>
        {paywallJsonLd && (
          <script type="application/ld+json">{JSON.stringify(paywallJsonLd)}</script>
        )}
        <BannerContainer>
          <RequestPageBanner request={request} />
        </BannerContainer>
        {isTabletLandscape && (
          <RightColumnFade shouldGate={shouldGate} gateRef={gateTopRef}>
            <Cta request={request} onShare={() => setIsShareModalOpen(true)} />
          </RightColumnFade>
        )}
        <DataPoints request={request} />
        <Columns>
          <Left>
            <Sidebar
              backgroundRef={backgroundRef}
              lookingForRef={lookingForRef}
              benefitsRef={benefitsRef}
              whoWeAreRef={whoWeAreRef}
              qaRef={qaRef}
              blockedKeys={shouldGate ? (["benefits", "who-we-are", "qa"] as any) : []}
              onSignupClick={() => {
                setSignupPrefillEmailCookie("");
                setPrevUrlCookie();
                dispatch(toggleLoginModal({ open: true, initialView: "signup" }));
              }}
              topOffset={topOffset}
            />
          </Left>
          <Center>
            <Summary>{request.requestable.summary}</Summary>
            <Section ref={backgroundRef} withNewlines topOffset={topOffset}>
              <Heading>{t("pages.request_for_startups_page.headings.background")}</Heading>
              {renderMarkdownTextWithDangerousHTML(request.requestable.background)}
            </Section>
            <Section ref={lookingForRef} topOffset={topOffset} marginBottom={-40}>
              <Heading>{t("pages.request_for_startups_page.headings.looking_for")}</Heading>
              <div>
                <Subheading>
                  {t("pages.request_for_startups_page.looking_for.subheading")}
                </Subheading>
                <ListHeading>
                  {t("pages.request_for_startups_page.looking_for.list_heading")}
                </ListHeading>
                {renderMarkdownText(request.requestable.solutions_of_interest)}
              </div>
            </Section>
            {shouldGate ? (
              <div>
                {/* Keep the top "Looking for" paragraph visible; gate detailed lists, TRL, eligibility */}
                <ContentClipFade maxHeightPx={90} className="paywalled-content" aria-hidden={true}>
                  {detailsContent}
                </ContentClipFade>
                <GateContainer ref={gateTopRef} className="progressive-gate-container">
                  <ProgressiveGate companyName={companyName} />
                </GateContainer>
              </div>
            ) : (
              detailsContent
            )}
          </Center>
          <RightColumnFade shouldGate={shouldGate} gateRef={gateTopRef} as={Right as any}>
            {!isTabletLandscape && (
              <Cta request={request} onShare={() => setIsShareModalOpen(true)} />
            )}
            {request.requestable.display_location && (
              <div>
                <h3>{t("pages.request_for_startups_page.headings.locations")}</h3>
                <p>{request.requestable.display_location}</p>
              </div>
            )}
            <RequestPageTagsContainer
              tags={request.partner_types?.map((type) => REQUEST_PARTNER_TYPE_LABELS[type])}
              heading="Eligible partners"
            />
            <RequestPageTagsContainer
              tags={request.areas_of_expertise}
              heading="Seeking partners focused on"
            />
            {/* Custom FAQs for Bayer Co-Lab Cambridge RFS */}
            {request.slug === "co-lab-cambridge" && <RequestFAQs request={request as any} />}
          </RightColumnFade>
        </Columns>
      </PageContainer>
    </>
  );
}

// Exported styles are also used in RequestForExpertsPage
export const Breadcrumb = styled.div`
  margin-top: 24px;
`;

export const Columns = styled.div`
  display: flex;
  flex-direction: row;
  gap: 20px;

  ${WINDOW_DIMENSIONS.TABLET_LANDSCAPE_MEDIA_QUERY} {
    flex-direction: column-reverse;
  }
`;

export const Left = styled.div`
  flex: 0 0 196px;

  ${WINDOW_DIMENSIONS.TABLET_LANDSCAPE_MEDIA_QUERY} {
    display: none;
  }
`;

export const Center = styled.div`
  flex: 1;

  display: flex;
  flex-direction: column;
  gap: 64px;

  padding-right: 46px;

  ${WINDOW_DIMENSIONS.TABLET_LANDSCAPE_MEDIA_QUERY} {
    padding-right: 0;
  }
`;

export const Right = styled.div`
  flex: 0 0 280px;
  display: flex;
  flex-direction: column;
  gap: 40px;
  h3 {
    padding: 0 24px;
    ${FONTS.HEADING_5_SEMIBOLD}
  }
  p {
    padding: 0 24px;
    ${FONTS.MEDIUM_2}
  }

  ${WINDOW_DIMENSIONS.TABLET_LANDSCAPE_MEDIA_QUERY} {
    flex: none;
    flex-direction: column-reverse;
    gap: 40px;
    width: 100%;
    margin-bottom: 40px;
  }
`;

export const Summary = styled.h2`
  margin: 0;
  ${FONTS.HEADING_3_REGULAR}
`;

export const Section = styled.section<{ topOffset?: number; marginBottom?: number }>`
  display: flex;
  flex-direction: column;
  gap: 24px;

  margin: 0;
  ${({ withNewlines }) => withNewlines && "white-space: pre-line;"}
  ${FONTS.REGULAR_1}
  scroll-margin-top: ${({ topOffset = 0 }) => `${topOffset}px`};
  margin-bottom: ${({ marginBottom = 0 }) => `${marginBottom}px`};

  ul {
    padding-left: 4px;
    white-space: normal; // negates the "white-space: pre-line;" effect above for lists
  }
`;

export const Heading = styled.h1`
  margin: 0;
  ${FONTS.HEADING_4_SEMIBOLD}
`;

export const Subheading = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: nowrap;
  margin: ${({ first }) => (first ? "4px" : "20px")} 0 8px;
  ${FONTS.SEMIBOLD_1}
`;

export const ListHeading = styled.span`
  margin-bottom: -16px;
`;

export const PlainList = styled.ul`
  padding-left: 22px !important;
  margin: 0 !important;
`;
const BannerContainer = styled.div`
  margin-top: 16px;
`;

const AnchorSpan = styled.span<{ topOffset?: number }>`
  scroll-margin-top: ${({ topOffset = 0 }) => `${topOffset}px`};
`;

const AnchorDiv = styled.div<{ topOffset?: number }>`
  scroll-margin-top: ${({ topOffset = 0 }) => `${topOffset}px`};
`;
